/**
 * Flujo oficial de documentos: /entregable/:id
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getConsultation,
  getPatient,
  getFollowUpContext,
  getPatientConsultations,
  getProfile,
  trackInformeEntregable,
} from "../lib/api";
import {
  buildInitialModuleState,
  DEFAULT_DELIVERABLE_TYPE,
  INFORME_CLINICO,
  INFORME_FAMILIAR,
  RESUMEN_CLINICO,
} from "../lib/deliverableConfig";
import DeliverableTypeSelector from "../components/deliverables/DeliverableTypeSelector";
import DeliverableModulesPanel from "../components/deliverables/DeliverableModulesPanel";
import DeliverableDocumentPreview from "../components/deliverables/DeliverableDocumentPreview";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Loader2, AlertCircle, Printer, FileDown, ArrowLeft } from "lucide-react";
import { useAuroraTour } from "../hooks/useAuroraTour";
import { TOUR_CONFIG } from "../lib/auroraTourSteps";
import { isUsClinicalHost } from "../lib/usClinicalHost";
import DemoTooltip from "../components/DemoTooltip";

export default function GenerarEntregablePage() {
  const { user } = useAuth();
  const isUsHost = isUsClinicalHost();
  const { id } = useParams();
  const navigate = useNavigate();
  const [consultation, setConsultation] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrintMode, setIsPrintMode] = useState(false);

  const [deliverableType, setDeliverableType] = useState(DEFAULT_DELIVERABLE_TYPE);
  const [moduleState, setModuleState] = useState(() => buildInitialModuleState(DEFAULT_DELIVERABLE_TYPE));
  const [localFields, setLocalFields] = useState({
    recomendacionesClinico: "",
    recomendacionesFamiliar: "",
  });
  const [familiarPreviousConsult, setFamiliarPreviousConsult] = useState(null);
  const [doctorProfile, setDoctorProfile] = useState(null);

  const initializedForId = useRef(null);
  const reportTypeAtPrintRef = useRef(null);

  useEffect(() => {
    initializedForId.current = null;
  }, [id]);

  useEffect(() => {
    setModuleState(buildInitialModuleState(deliverableType));
  }, [deliverableType]);

  useEffect(() => {
    const allowedTypes = new Set([INFORME_CLINICO, INFORME_FAMILIAR, RESUMEN_CLINICO]);
    const onAfterPrint = () => {
      setIsPrintMode(false);
      const rt = reportTypeAtPrintRef.current;
      reportTypeAtPrintRef.current = null;
      if (rt && allowedTypes.has(rt)) {
        if (user?.email === "demo@aurorageriatrica.com") { return; }
        trackInformeEntregable(rt).catch(() => {});
      }
    };
    window.addEventListener("afterprint", onAfterPrint);
    return () => window.removeEventListener("afterprint", onAfterPrint);
  }, [user]);

  useEffect(() => {
    if (!id) {
      setError("Identificador de consulta no válido");
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setError(null);
    setConsultation(null);
    setPatient(null);

    const loadData = async () => {
      try {
        const consultRes = await getConsultation(id);
        const c = consultRes?.data ?? consultRes;
        if (!alive) return;
        if (!c) {
          setError("Consulta no encontrada");
          return;
        }
        setConsultation(c);

        const pid = c?.paciente_id;
        if (pid) {
          try {
            const pRes = await getPatient(pid);
            const p = pRes?.data ?? pRes;
            if (alive && p) setPatient(p);
          } catch (patientErr) {
            console.error(
              "[GenerarEntregablePage] Error al cargar paciente:",
              pid,
              patientErr?.response?.data ?? patientErr?.message ?? patientErr
            );
            if (alive) toast.error("No se pudo cargar el paciente; el documento puede mostrarse incompleto");
          }
        }
      } catch (loadErr) {
        console.error(
          "[GenerarEntregablePage] Error al cargar consulta:",
          id,
          loadErr?.response?.status,
          loadErr?.response?.data ?? loadErr?.message ?? loadErr
        );
        if (alive) {
          setError("Error al cargar datos");
          toast.error("Error al cargar la consulta");
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadData();
    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    let alive = true;
    if (!consultation) {
      setDoctorProfile(null);
      return;
    }
    getProfile()
      .then((res) => {
        if (alive) setDoctorProfile(res?.data ?? res ?? null);
      })
      .catch(() => {
        if (alive) setDoctorProfile(null);
      });
    return () => {
      alive = false;
    };
  }, [consultation]);

  useEffect(() => {
    if (!consultation || initializedForId.current === id) return;
    initializedForId.current = id;
    setDeliverableType(DEFAULT_DELIVERABLE_TYPE);
    setModuleState(buildInitialModuleState(DEFAULT_DELIVERABLE_TYPE));
    setLocalFields({
      recomendacionesClinico: "",
      recomendacionesFamiliar: "",
    });
  }, [consultation, id]);

  useEffect(() => {
    let alive = true;
    const pid = consultation?.paciente_id;
    const curId = String(consultation?.id ?? consultation?._id ?? "");
    if (!pid || deliverableType !== INFORME_FAMILIAR || !curId) {
      setFamiliarPreviousConsult(null);
      return;
    }
    (async () => {
      try {
        const [ctxRes, listRes] = await Promise.all([
          getFollowUpContext(pid),
          getPatientConsultations(pid),
        ]);
        const ctx = ctxRes?.data ?? ctxRes;
        const list = listRes?.data ?? listRes ?? [];
        const last = ctx?.last_consultation;
        if (last && String(last.id) !== curId) {
          if (alive) setFamiliarPreviousConsult(last);
          return;
        }
        const idx = list.findIndex((x) => String(x.id ?? x._id) === curId);
        if (idx >= 0 && list[idx + 1]) {
          const p = list[idx + 1];
          if (alive) {
            setFamiliarPreviousConsult({
              id: String(p.id ?? p._id),
              fecha_consulta: p.fecha_consulta,
              vitals: p.vitals,
              escalas: p.escalas,
            });
          }
          return;
        }
        if (alive) setFamiliarPreviousConsult(null);
      } catch {
        if (alive) setFamiliarPreviousConsult(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [consultation, deliverableType]);

  const onLocalChange = useCallback((key, value) => {
    setLocalFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleToggleModule = useCallback((moduleId, on) => {
    setModuleState((prev) => ({ ...prev, [moduleId]: on }));
  }, []);

  const handlePrint = () => {
    reportTypeAtPrintRef.current = deliverableType;
    setIsPrintMode(true);
    setTimeout(() => window.print(), 0);
  };

  useAuroraTour(TOUR_CONFIG.pdf.key, TOUR_CONFIG.pdf.steps, {
    enabled: !loading && !error && !!consultation && !isUsHost,
    delay: 850,
  });

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="generar-entregable-loading">
        <Card className="border-slate-200 shadow-sm bg-white">
          <CardContent className="pt-10 pb-10 flex flex-col items-center gap-4 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-sky-600" aria-hidden />
            <p className="text-sm font-medium text-slate-800">Cargando consulta…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 px-4">
        <Card className="border-slate-200 shadow-sm bg-white">
          <CardContent className="pt-8 pb-8 px-6 text-center">
            <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" aria-hidden />
            <h1 className="text-lg font-semibold text-slate-900">No se pudo generar el documento</h1>
            <p className="text-sm text-slate-600 mt-2">{error || "Consulta no encontrada"}</p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Button variant="outline" className="border-slate-200" onClick={() => navigate(-1)}>
                Volver
              </Button>
              <Button variant="outline" asChild className="border-slate-200">
                <Link to="/pacientes">Ir a pacientes</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const nombre =
    patient && `${patient.nombres || ""} ${patient.apellidos || ""}`.trim()
      ? `${patient.nombres || ""} ${patient.apellidos || ""}`.trim()
      : "Paciente";
  const edadStr = patient?.edad != null ? `${patient.edad} años` : "—";
  const fecha = consultation?.fecha_consulta
    ? new Date(consultation.fecha_consulta).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "—";

  const previewProps = {
    consultation,
    patient,
    deliverableType,
    moduleState,
    localFields,
    onLocalChange,
    viewerUser: user,
    doctorProfile,
    treatingPhysicianName:
      deliverableType === INFORME_FAMILIAR ? user?.nombre?.trim() || undefined : undefined,
    familiarPreviousConsult: deliverableType === INFORME_FAMILIAR ? familiarPreviousConsult : null,
  };

  if (isPrintMode) {
    return (
      <div id="deliverable-print-root" className="min-h-screen bg-white p-6 sm:p-10 print:p-6">
        <DeliverableDocumentPreview {...previewProps} variant="print" className="mx-auto max-w-4xl border-0 shadow-none min-h-0" />
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto pb-10 space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 sm:px-6 sm:py-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 space-y-1">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Documento de la consulta</h1>
            <p className="text-sm text-slate-600">Elija el tipo de documento y, al terminar, imprima.</p>
            <p className="text-xs text-slate-500">
              <span className="font-medium text-slate-700">{nombre}</span>
              <span className="text-slate-300 mx-1.5">·</span>
              {edadStr}
              <span className="text-slate-300 mx-1.5">·</span>
              {fecha}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0" data-aurora-tour="pdf-download">
            <DemoTooltip message="Aurora genera informes PDF profesionales automáticamente — informe clínico, resumen familiar y plan terapéutico. Todos incluyen el logo y datos de tu clínica." position="top" />
            <Button variant="outline" size="sm" className="border-slate-200" type="button" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2 text-sky-600" />
              Imprimir
            </Button>
            <Button variant="outline" size="sm" className="border-slate-200 opacity-60" type="button" disabled title="Próximamente">
              <FileDown className="h-4 w-4 mr-2 text-slate-500" />
              Guardar documento
            </Button>
            <Button variant="outline" size="sm" className="border-slate-200" type="button" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2 text-slate-600" />
              Volver
            </Button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100" data-aurora-tour="pdf-types">
          <p className="text-[11px] font-medium text-slate-500 mb-2 uppercase tracking-wide">Tipo de documento</p>
          <DeliverableTypeSelector value={deliverableType} onChange={setDeliverableType} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 lg:items-stretch gap-4 lg:gap-6">
        <aside className="order-2 lg:order-1 lg:col-span-2 min-h-0 print:hidden">
          <DeliverableModulesPanel
            deliverableType={deliverableType}
            moduleState={moduleState}
            onToggle={handleToggleModule}
            className="border-slate-200/70 bg-slate-50/50 shadow-sm p-3 sm:p-3.5"
          />
        </aside>

        <main className="order-1 lg:order-2 lg:col-span-10 min-w-0 print:col-span-12">
          <DeliverableDocumentPreview {...previewProps} variant="edit" className="lg:shadow-md lg:ring-1 lg:ring-slate-200/90 print:shadow-none" />
        </main>
      </div>
    </div>
  );
}
